/**
 * PATH: packages/ui/src/kanban/components/CardCashFlowSection.tsx
 * FILE_ID: UI-KANBAN-CASHFLOW-001
 * PURPOSE: Cash-flow editor section inside `<CardDetailDrawer>` — exposes
 *          amount + direction for a single Card, and embeds the shared
 *          CardBlockersSection (predecessors / "Blocked by"). Only renders
 *          when the parent board has `cash_flow_enabled = true`. The blocker
 *          CRUD now lives in CardBlockersSection so non-cash-flow boards get
 *          it too (rendered standalone by CardTabContent).
 * WHY: The user asked for a Gantt/Kanban hybrid that visualises cash
 *      in/out across a project plan (FEAT-2026-05-18-KANBAN-CASHFLOW).
 *      MR-A landed the BE shape (Card.amount_cents / .cash_direction +
 *      CardDependency join table). This file is the FE *write* surface;
 *      MR-C reads these fields into the new Cash Flow view tab.
 * FLOW:
 *   ┌──────────────────┐    ┌─────────────────────┐    ┌──────────────────────┐
 *   │ operator edits   │ →  │ api.updateCard      │ →  │ react-query invalidate│
 *   │ amount/direction │    │ (amount_cents /     │    │ → drawer re-renders   │
 *   │                  │    │  cash_direction)    │    │   with fresh data     │
 *   └──────────────────┘    └─────────────────────┘    └──────────────────────┘
 * DEPENDENCIES:
 *   - @tanstack/react-query: patch amount/direction + invalidate the board
 *     cards cache so peers refresh
 *   - ../api: KanbanApi (updateCard)
 *   - ../types: Card, CashDirection, KanbanApi
 *   - ./card-detail/CardBlockersSection: embedded (variant='bare') for the
 *     predecessor / "Blocked by" CRUD
 * RELATED:
 *   - CardDetailDrawer.tsx (UI-KANBAN-DRAWER-001): mounts this section
 *     when `cashFlowEnabled` is true; passes `card`, `api`, `boardId`,
 *     `boardCards`, `onChanged`
 *   - CardBlockersSection.tsx (UI-KANBAN-CMP-CARD-BLOCKERS-001): the shared
 *     dependency CRUD, rendered standalone on non-cash-flow boards
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDownRight, ArrowUpRight, Info } from 'lucide-react'

import type { KanbanApi } from '../api'
import type { Card, CashDirection } from '../types'
import { CardBlockersSection } from './card-detail/CardBlockersSection'

export interface CardCashFlowSectionProps {
  card: Card
  api: KanbanApi
  /** Parent board id — required to scope the dependency query and
   *  shortlist eligible predecessors from `boardCards`. */
  boardId: string | undefined
  /** All other cards on this board. The predecessor picker filters
   *  this list to "not the current card AND not already a predecessor". */
  boardCards: Card[] | undefined
  /** Called after a successful amount/direction patch so the parent
   *  drawer can refresh its local view AND its react-query cache. */
  onChanged?: (card: Card) => void
}

const DIRECTION_OPTIONS: Array<{
  value: CashDirection
  label: string
  Icon: typeof ArrowDownRight
  tone: 'red' | 'green' | 'neutral'
}> = [
  { value: 'out', label: 'Out',  Icon: ArrowUpRight,  tone: 'red' },
  { value: 'in',  label: 'In',   Icon: ArrowDownRight, tone: 'green' },
  { value: 'info', label: 'Info', Icon: Info,         tone: 'neutral' },
]

const TONE_CLS: Record<'red' | 'green' | 'neutral', { active: string; idle: string }> = {
  red:     { active: 'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-950/30 dark:text-red-300 dark:ring-red-900',
             idle:   'text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800' },
  green:   { active: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-900',
             idle:   'text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800' },
  neutral: { active: 'bg-neutral-100 text-neutral-700 ring-1 ring-neutral-300 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-700',
             idle:   'text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800' },
}

/** Cents ↔ dollars conversion at the form boundary. We never store
 *  floating-point dollars on the wire — see `CardCreate.amount_cents`. */
function centsToDollars(c: number | null | undefined): string {
  if (c == null) return ''
  return (c / 100).toFixed(2)
}
function dollarsToCents(s: string): number | null {
  const trimmed = s.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100)
}

export function CardCashFlowSection({
  card,
  api,
  boardId,
  boardCards,
  onChanged,
}: CardCashFlowSectionProps) {
  const qc = useQueryClient()
  const [amountStr, setAmountStr] = useState<string>(centsToDollars(card.amount_cents))
  const [direction, setDirection] = useState<CashDirection | null>(card.cash_direction ?? null)
  const [error, setError] = useState<string | null>(null)

  const patchScalarMut = useMutation({
    mutationFn: async (patch: { amount_cents?: number | null; cash_direction?: CashDirection | null }) => {
      // Reuse the drawer's CAS contract via `updated_at` if available.
      return api.updateCard(card.id, patch as never)
    },
    onSuccess: (updated) => {
      setError(null)
      onChanged?.(updated)
      if (boardId) {
        void qc.invalidateQueries({ queryKey: ['kanban', 'cards', boardId] })
      }
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Could not save')
    },
  })

  function commitAmount() {
    const next = dollarsToCents(amountStr)
    if (next === card.amount_cents) return
    patchScalarMut.mutate({ amount_cents: next })
  }

  function commitDirection(next: CashDirection) {
    if (next === direction) {
      setDirection(null)
      patchScalarMut.mutate({ cash_direction: null })
      return
    }
    setDirection(next)
    patchScalarMut.mutate({ cash_direction: next })
  }

  return (
    <section
      data-testid="card-cashflow-section"
      className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40 px-3 py-3 space-y-3"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">
          Cash Flow
        </h3>
        {patchScalarMut.isPending && (
          <span className="text-[10px] text-neutral-400">Saving…</span>
        )}
      </header>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-neutral-500">Amount</span>
          <input
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            onBlur={commitAmount}
            placeholder="0.00"
            className="rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1.5 text-xs"
            disabled={patchScalarMut.isPending}
            aria-label="Cash flow amount in dollars"
          />
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-neutral-500">Direction</span>
          <div className="flex gap-1" role="radiogroup" aria-label="Cash flow direction">
            {DIRECTION_OPTIONS.map(({ value, label, Icon, tone }) => {
              const active = direction === value
              const cls = TONE_CLS[tone]
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => commitDirection(value)}
                  disabled={patchScalarMut.isPending}
                  className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium ${active ? cls.active : cls.idle}`}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Blocker / predecessor CRUD — shared with non-cash-flow boards.
          variant='bare' keeps the cash-flow board's look unchanged. */}
      <CardBlockersSection
        card={card}
        api={api}
        boardId={boardId}
        boardCards={boardCards}
        variant="bare"
      />

      {error && (
        <p role="alert" className="text-[11px] text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </section>
  )
}

export const _internals = { centsToDollars, dollarsToCents }
